import type { RequestFn } from '../http';

export type PipelineStage =
  | 'REQUESTED'
  | 'OFFERED'
  | 'ORDERED'
  | 'RECEIVED'
  | 'READY'
  | 'DELIVERING'
  | 'DELIVERED'
  | 'REJECTED'
  | 'UNKNOWN';

export type PipelineSourceType =
  | 'TECHNICIAN_REQUEST'
  | 'DIRECT_RECEIPT'
  | 'DIRECT_DELIVERY';

export interface PipelineUser {
  id: string;
  displayName: string;
  email: string;
}

export interface PipelineEntry {
  id: string;
  sourceType: PipelineSourceType;
  technicianRequestId: string | null;
  goodsReceiptId: string | null;
  submissionId: string | null;
  title: string;
  requestedBy: PipelineUser | null;
  stage: PipelineStage;
  trStatus: string | null;
  grStatus: string | null;
  submissionStatus: string | null;
  weclappQuoteId: string | null;
  weclappSalesOrderId: string | null;
  weclappPurchaseOrderId: string | null;
  weclappTicketId: string | null;
  weclappTicketNumber: string | null;
  customerName: string | null;
  expectedDeliveryAt: string | null;
  deliveredAt: string | null;
  hasSignature: boolean;
  daysInStage: number;
  isOverdue: boolean;
  lastActivityAt: string;
  createdAt: string;
}

export interface PipelineListResponse {
  entries: PipelineEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface PipelineDetailResponse {
  id: string;
  title: string;
  sourceType: PipelineSourceType;
  stage: PipelineStage;
  daysInStage: number;
  isOverdue: boolean;
  hasSignature: boolean;
  customerName: string | null;
  weclappTicketId: string | null;
  weclappTicketNumber: string | null;
  weclappQuoteId: string | null;
  weclappSalesOrderId: string | null;
  weclappPurchaseOrderId: string | null;
  expectedDeliveryAt: string | null;
  deliveredAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  technicianRequest: {
    id: string;
    title: string;
    description: string;
    status: string;
    category: string;
    amount: string | null;
    adminNote: string | null;
    orderedAt: string | null;
    confirmedAt: string | null;
    expectedDeliveryAt: string | null;
    weclappQuoteId: string | null;
    weclappSalesOrderId: string | null;
    orderTicketId: string | null;
    orderTicketUrl: string | null;
    requestedBy: PipelineUser;
  } | null;
  goodsReceipt: {
    id: string;
    status: string;
    supplierName: string | null;
    deliveryNoteNo: string | null;
    weclappPurchaseOrderId: string | null;
    cwPurchaseOrderId: number | null;
    postedAt: string | null;
    itemCount: number;
  } | null;
  submission: {
    id: string;
    status: string;
    weclappTicketId: string | null;
    weclappTicketNumber: string | null;
    customerNamePrint: string | null;
    signatureImage: string | null;
    submittedAt: string | null;
    itemCount: number;
    technician: PipelineUser;
    assetSyncLogs: {
      id: string;
      submissionItemId: string;
      status: string;
      cwConfigurationId: number | null;
      articleName: string;
      serialNumber: string | null;
      assetTag: string | null;
      errorMessage: string | null;
      syncedAt: string | null;
    }[];
  } | null;
}

export interface PipelineStatsResponse {
  stats: { stage: PipelineStage; count: number; overdue: number }[];
}

export interface PipelineListFilters {
  stage?: PipelineStage;
  sourceType?: PipelineSourceType;
  overdueOnly?: boolean;
  userId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function createProcurementApi(request: RequestFn) {
  return {
    async getPipelineEntries(filters: PipelineListFilters = {}): Promise<PipelineListResponse> {
      const params = new URLSearchParams();
      if (filters.stage) params.set('stage', filters.stage);
      if (filters.sourceType) params.set('sourceType', filters.sourceType);
      if (filters.overdueOnly) params.set('overdueOnly', 'true');
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.search) params.set('search', filters.search);
      if (filters.limit != null) params.set('limit', String(filters.limit));
      if (filters.offset != null) params.set('offset', String(filters.offset));
      const qs = params.toString();
      return request<PipelineListResponse>(`/procurement/pipeline${qs ? `?${qs}` : ''}`);
    },

    async getPipelineEntry(id: string): Promise<PipelineDetailResponse> {
      return request<PipelineDetailResponse>(`/procurement/pipeline/${id}`);
    },

    async getPipelineStats(): Promise<PipelineStatsResponse> {
      return request<PipelineStatsResponse>('/procurement/pipeline/stats');
    },

    async refreshPipeline(): Promise<{ ok: boolean; refreshedAt: string }> {
      return request<{ ok: boolean; refreshedAt: string }>('/procurement/pipeline/refresh', {
        method: 'POST',
      });
    },

    async triggerWeclappPoll(): Promise<{ ok: boolean; result: { trQuoteUpdated: number; trOrderUpdated: number; grPurchaseOrderUpdated: number; errors: number; skippedNoConfig: boolean }; triggeredAt: string }> {
      return request('/procurement/weclapp-poll', { method: 'POST' });
    },

    async pollWeclappEntries(trIds: string[], grIds: string[]): Promise<{ ok: boolean }> {
      return request('/procurement/weclapp-poll-entries', { method: 'POST', body: JSON.stringify({ trIds, grIds }) });
    },

    async updatePipelineEntry(id: string, data: { expectedDeliveryAt?: string | null; adminNote?: string | null }): Promise<{ ok: boolean }> {
      return request<{ ok: boolean }>(`/procurement/pipeline/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  };
}
